import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { figuresFor, money as pickMoney, units as pickUnits, type Basis, type Count, type Scope } from "../api/figures";
import { marginBreakdown } from "../api/margin";
import {
  fxRate,
  type Currency,
  type GlobalFX,
  type InvoiceAggregateDTO,
  type InvoiceWindowDTO,
  type ReportingClient,
} from "../api/reporting";
import {
  DEFAULT_PRESETS,
  presetSelection,
  resolveColumn,
  type Cell,
  type Comparison,
  type PeriodColumn,
  type PresetKey,
  type Selection,
} from "../pages/sales/periods";
import { useSalesBasis } from "./basis";

/**
 * The state behind a sales grid: which periods are selected, how each is
 * compared, and the columns that follow.
 *
 * One hook for both pages. The analysis page selects several presets at once;
 * the dashboard board selects one. Everything downstream — the fetch, the
 * grid, the drill-down, the breakdown panels — takes `columns` and does not
 * know which page it is on.
 */
export function useSalesWindows(options: { mode: "multi" | "single"; defaults?: PresetKey[] }) {
  const [comparison, setComparison] = useState<Comparison>("previous");
  const [selected, setSelected] = useState<Selection[]>(() =>
    (options.defaults ?? (options.mode === "single" ? ["this-month"] : DEFAULT_PRESETS)).map((preset) =>
      presetSelection(preset),
    ),
  );

  const toggle = useCallback(
    (selection: Selection) => {
      setSelected((current) => {
        const has = current.some((entry) => entry.key === selection.key);
        if (options.mode === "single") return has ? current : [selection];
        if (has) return current.length === 1 ? current : current.filter((entry) => entry.key !== selection.key);
        return [...current, selection];
      });
    },
    [options.mode],
  );

  const remove = useCallback((key: string) => {
    setSelected((current) => (current.length === 1 ? current : current.filter((entry) => entry.key !== key)));
  }, []);

  const columns = useMemo(
    () => selected.map((selection) => resolveColumn(selection, comparison)),
    [selected, comparison],
  );

  return { selected, toggle, remove, comparison, setComparison, columns };
}

/* --------------------------------------------------------------- fetch --- */

/** A fetched window, keyed by its dates rather than its label. */
export interface WindowData {
  byClient: InvoiceAggregateDTO[];
  byCurrency: InvoiceAggregateDTO[];
}

const TTL_MS = 5 * 60_000;
const cache = new Map<string, { at: number; data: WindowData }>();

const identity = (from: string, to: string) => `${from}:${to}`;

/**
 * Loads every window the columns need, in one request for whatever the cache
 * lacks.
 *
 * Cached per window, by its dates. Adding a chip fetches that chip; flipping
 * the comparison fetches only the compared windows that are new; and switching
 * basis or scope fetches nothing at all, because the figures object carries
 * every basis and both scopes. The old page keyed its single query on the
 * whole configuration, so any change refetched everything.
 */
export function useWindowData(client: ReportingClient, columns: PeriodColumn[], enabled = true) {
  const windows = useMemo(
    () => columns.flatMap((column) => [column.current, column.compare]),
    [columns],
  );
  const ids = windows.map((window) => identity(window.period.from, window.period.to));

  return useQuery({
    queryKey: ["sales-windows", ...ids],
    queryFn: async () => {
      const now = Date.now();
      const missing = windows.filter((window) => {
        const hit = cache.get(identity(window.period.from, window.period.to));
        return !hit || now - hit.at > TTL_MS;
      });
      // Two selections can resolve to the same dates — a custom span equal to
      // a preset — and the server should be asked once.
      const unique = [...new Map(missing.map((w) => [identity(w.period.from, w.period.to), w])).values()];
      if (unique.length > 0) {
        const fetched: InvoiceWindowDTO[] = await client.aggregateWindows(
          unique.map((window) => ({ key: identity(window.period.from, window.period.to), from: window.period.from, to: window.period.to })),
        );
        for (const entry of fetched) {
          cache.set(entry.key, { at: now, data: { byClient: entry.by_client ?? [], byCurrency: entry.by_currency ?? [] } });
        }
      }
      const out = new Map<string, WindowData>();
      for (const window of windows) {
        const hit = cache.get(identity(window.period.from, window.period.to));
        out.set(window.key, hit?.data ?? { byClient: [], byCurrency: [] });
      }
      return out;
    },
    enabled: enabled && columns.length > 0,
    staleTime: TTL_MS,
  });
}

/* ---------------------------------------------------------------- rows --- */

export interface GridRow {
  id: number;
  no?: string | null;
  name: string;
  currency: Currency | "";
  /** Per window key, in the row's own currency. */
  values: Record<string, Cell>;
  /** A currency total rather than a client. */
  synthetic?: boolean;
}

export const TOTAL_CAD_ID = -1;
export const TOTAL_USD_ID = 0;

const cellOf = (aggregate: InvoiceAggregateDTO, fx: GlobalFX | undefined, currency: Currency | null, basis: Basis, scope: Scope, count: Count): Cell => {
  const figures = figuresFor(aggregate, scope);
  const breakdown = marginBreakdown(aggregate, fxRate(fx, currency));
  return {
    gross: pickMoney(figures, basis),
    units: pickUnits(figures, basis, count),
    ncm: breakdown.netMargin,
    ncmPercent: breakdown.netMarginPercent,
  };
};

/**
 * The grid's rows: the two currency totals first, then one per client.
 *
 * Built at render from the fetched windows and the session's basis and scope,
 * so changing either is instant. The totals are the currency aggregates, not a
 * sum of the client rows: net margin depends on costs that are loaded per
 * currency and per client from different tables, and the two do not have to
 * agree to the cent.
 */
export function buildRows(
  data: Map<string, WindowData> | undefined,
  columns: PeriodColumn[],
  fx: GlobalFX | undefined,
  basis: Basis,
  scope: Scope,
  count: Count,
): GridRow[] {
  const rows = new Map<number, GridRow>();
  rows.set(TOTAL_CAD_ID, { id: TOTAL_CAD_ID, no: "TOTCAD", name: "Total CAD", currency: "CAD", values: {}, synthetic: true });
  rows.set(TOTAL_USD_ID, { id: TOTAL_USD_ID, no: "TOTUSD", name: "Total USD", currency: "USD", values: {}, synthetic: true });
  if (!data) return [...rows.values()];

  for (const column of columns) {
    for (const window of [column.current, column.compare]) {
      const entry = data.get(window.key);
      if (!entry) continue;
      for (const aggregate of entry.byCurrency) {
        const id = aggregate.currency === "USD" ? TOTAL_USD_ID : TOTAL_CAD_ID;
        rows.get(id)!.values[window.key] = cellOf(aggregate, fx, aggregate.currency ?? null, basis, scope, count);
      }
      for (const aggregate of entry.byClient) {
        const clientId = aggregate.client?.cli_id;
        if (clientId === undefined) continue;
        const currency = (aggregate.client?.cli_currency ?? aggregate.currency ?? "") as Currency | "";
        const row =
          rows.get(clientId) ??
          rows
            .set(clientId, {
              id: clientId,
              no: aggregate.client?.cli_no ?? null,
              name: aggregate.client?.cli_name1 ?? `#${clientId}`,
              currency,
              values: {},
            })
            .get(clientId)!;
        row.values[window.key] = cellOf(aggregate, fx, currency || null, basis, scope, count);
      }
    }
  }
  return [...rows.values()];
}

/** The two currency-total rows and the client rows, ready for a grid. */
export function useSalesRows(client: ReportingClient, columns: PeriodColumn[], fx: GlobalFX | undefined, enabled = true) {
  const { basis, scope, count } = useSalesBasis();
  const query = useWindowData(client, columns, enabled);
  const rows = useMemo(() => buildRows(query.data, columns, fx, basis, scope, count), [query.data, columns, fx, basis, scope, count]);
  return { ...query, rows };
}
